import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  LineChart, Line, XAxis, YAxis,
  BarChart, Bar,
} from "recharts";
import { Camera, CheckCircle2, WifiOff, Cpu, Aperture, Radio, Eye, Heart, ArrowRight } from "lucide-react";

const STATUS_COLOR = {
  Active:  "hsl(var(--success))",
  Offline: "hsl(var(--muted-foreground))",
  Error:   "hsl(var(--destructive))",
};

const TYPE_COLOR = {
  PTZ:     "hsl(var(--primary))",
  Dome:    "hsl(217 91% 60%)",
  Fixed:   "hsl(var(--muted-foreground))",
  "360":   "hsl(var(--warning))",
  Thermal: "hsl(var(--destructive))",
};

const Pill = ({ icon: Icon, label, value, tone = "default", sub, testid }) => {
  const map = {
    default: "text-foreground",
    success: "sev-low",
    danger:  "sev-critical",
    warn:    "sev-medium",
    primary: "text-[hsl(var(--primary))]",
  };
  return (
    <div className="panel-elevated p-2.5 flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 shrink-0 ${map[tone]}`} />
      <div className="min-w-0">
        <div className="micro-label leading-none">{label}</div>
        <div className={`font-display font-black text-lg leading-tight ${map[tone]}`} data-testid={`${testid}-value`}>
          {value}
        </div>
        {sub && <div className="micro-label text-[9px] mt-0.5 text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
};

const StatusDonut = ({ data = [] }) => {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) {
    return <div className="h-36 flex items-center justify-center text-[11px] text-muted-foreground">No cameras.</div>;
  }
  return (
    <div className="relative h-36" data-testid="cameras-status-donut">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="status" innerRadius={38} outerRadius={56} paddingAngle={1} isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.status} fill={STATUS_COLOR[d.status]} stroke="hsl(var(--surface))" strokeWidth={1.5} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="font-mono text-[9px] text-muted-foreground tracking-wide">Total</div>
        <div className="font-display font-black text-lg leading-none">{total}</div>
      </div>
    </div>
  );
};

const TypeBar = ({ data = [] }) => (
  <div className="h-36" data-testid="cameras-type-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
        <XAxis dataKey="type" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} />
        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} allowDecimals={false} width={26} />
        <Tooltip contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }} />
        <Bar dataKey="count" radius={[1, 1, 0, 0]} isAnimationActive={false}>
          {data.map((d) => (
            <Cell key={d.type} fill={TYPE_COLOR[d.type] || "hsl(var(--primary))"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const HealthTimeline = ({ data = [] }) => {
  const series = data.map((d) => ({
    t: new Date(d.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    uptime: d.uptime_pct,
  }));
  return (
    <div className="h-36" data-testid="cameras-health-timeline">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 6, left: -22, bottom: 0 }}>
          <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} interval="preserveStartEnd" />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} unit="%" width={32} domain={[60, 100]} />
          <Tooltip contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }} />
          <Line type="monotone" dataKey="uptime" stroke="hsl(var(--success))" strokeWidth={1.75} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const ZoneCoverageTable = ({ rows = [] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[11px]" data-testid="cameras-zone-coverage">
      <thead>
        <tr className="text-left font-mono text-[10px] text-muted-foreground tracking-wide border-b border-border">
          <th className="py-1.5 px-2 font-medium">Zone</th>
          <th className="py-1.5 px-2 font-medium text-right">Total</th>
          <th className="py-1.5 px-2 font-medium text-right">Active</th>
          <th className="py-1.5 px-2 font-medium text-right">Blind Spots</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.zone_id}
            className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
            data-testid={`cameras-zone-row-${r.zone_id}`}
          >
            <td className="py-1.5 px-2">{r.zone_name}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.total}</td>
            <td className="py-1.5 px-2 text-right font-mono sev-low">{r.active}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${r.blind_spots > 0 ? "sev-critical" : "text-muted-foreground"}`}>
              {r.blind_spots}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CameraInfrastructurePanel = ({ data }) => {
  const navigate = useNavigate();
  const d = data || { kpis: {}, status_breakdown: [], type_breakdown: [], health_timeline: [], zone_coverage: [] };
  const k = d.kpis || {};

  return (
    <section className="space-y-3" data-testid="dashboard-section-cameras">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold tracking-tight text-xs">Camera Infrastructure Analytics</h2>
        </div>
        <button
          onClick={() => navigate("/cameras")}
          className="btn-tac !px-2 !py-1 text-[10px]"
          data-testid="cameras-open-page-btn"
        >
          Open Cameras <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <Pill icon={Camera}        label="Total Cameras"  value={k.total ?? 0}                                tone="default" testid="cam-kpi-total" />
        <Pill icon={CheckCircle2}  label="Active Streams" value={k.active ?? 0}                               tone="success" testid="cam-kpi-active" />
        <Pill icon={WifiOff}       label="Inactive"       value={k.inactive ?? 0}                             tone="danger"  testid="cam-kpi-inactive" />
        <Pill icon={Cpu}           label="AI Enabled"     value={k.ai_enabled ?? 0}                           tone="primary" testid="cam-kpi-ai" />
        <Pill icon={Aperture}      label="PTZ"            value={k.ptz_count ?? 0}                            tone="default" testid="cam-kpi-ptz" />
        <Pill icon={Aperture}      label="360°"           value={k.rotational_360 ?? 0}                       tone="warn"    testid="cam-kpi-360" />
        <Pill icon={Radio}         label="RTSP Streams"   value={k.rtsp_streams ?? 0}                         tone="default" testid="cam-kpi-rtsp" />
        <Pill icon={Radio}         label="RTMP Streams"   value={k.rtmp_streams ?? 0}                         tone="default" testid="cam-kpi-rtmp" />
        <Pill icon={Eye}           label="Low Visibility" value={k.low_visibility ?? 0}                       tone="warn"    testid="cam-kpi-lowvis" />
        <Pill icon={Heart}         label="Health Score"   value={`${(k.health_score ?? 0).toFixed?.(1) ?? 0}%`} tone="success" testid="cam-kpi-health" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2">Camera Status</div>
          <StatusDonut data={d.status_breakdown} />
          <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
            {d.status_breakdown.map((s) => (
              <span key={s.status} className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
                <span className="w-2 h-2" style={{ background: STATUS_COLOR[s.status] }} />
                {s.status} · {s.count}
              </span>
            ))}
          </div>
        </div>

        <div className="panel p-3">
          <div className="h-widget mb-2">Camera Type Distribution</div>
          <TypeBar data={d.type_breakdown} />
        </div>

        <div className="panel p-3">
          <div className="h-widget mb-2">Health Timeline (Uptime %)</div>
          <HealthTimeline data={d.health_timeline} />
        </div>
      </div>

      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget">Zone-Wise Camera Coverage</div>
          <span className="micro-label">Blind Spots First</span>
        </div>
        <ZoneCoverageTable rows={d.zone_coverage} />
      </div>
    </section>
  );
};

export default CameraInfrastructurePanel;
